import { useAppForm } from "@/components/app-form";
import { BillingInfoFormFields } from "@/components/form/billing-info-form";
import { useMuiModal } from "@/components/modal";
import {
  BackButton,
  Footer,
  Header,
  Page,
  PageContent,
  PageTitle,
} from "@/components/page";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { api } from "@/lib/server";
import {
  postBillingInfoSchema,
  PostBillingInfoType,
} from "@common/schemas/billing-info";
import { revalidateLogic } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import {
  InvoiceErrorModal,
  InvoiceSentModal,
} from "@/features/invoice/invoice-result-modals";

export const Route = createFileRoute("/invoice-generation-screen/$nonce")({
  component: RouteComponent,
  // "start": opened from the wash start guide, which already showed the
  // "thank you" part, so every exit goes back to the start screen
  validateSearch: z.object({ then: z.enum(["start"]).optional().catch(undefined) }),
});

function RouteComponent() {
  useAudioSlot("page.invoice-generation");
  const { nonce } = Route.useParams();
  const { then } = Route.useSearch();
  const language = useLanguage();
  const { t } = useTranslation();
  const { showModal } = useMuiModal();
  const navigate = useNavigate();
  const networkStatus = useNetworkStatus();
  const leave = () =>
    then === "start"
      ? navigate({ to: "/start-screen", reloadDocument: true })
      : navigate({ to: "/bubbles-loves-you-screen" });

  const form = useAppForm({
    defaultValues: {
      billingInfo: {
        username: undefined as string | undefined,
        taxnum: undefined as string | undefined,
        city: undefined as string | undefined,
        zip: "" as string | undefined,
        street: undefined as string | undefined,
        groupTaxNum: undefined as string | undefined,
        registrationNumber: undefined as string | undefined,
        billingType: "CORPORATE",
        preferredTaxnum: "regular",
        email: undefined as string | undefined,
      } as PostBillingInfoType,
    },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: z.object({ billingInfo: postBillingInfoSchema }),
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await api.generateKioskInvoice({
          ...value.billingInfo,
          paymentId: nonce,
          lang: language,
          taxnum: value.billingInfo.taxnum ?? "",
          groupTaxNum: value.billingInfo.groupTaxNum,
          registrationNumber: value.billingInfo.registrationNumber,
          preferredTaxnum: value.billingInfo.preferredTaxnum,
        });

        await showModal(
          <InvoiceSentModal
            queued={
              !!result.queued || networkStatus.data === "disconnected"
            }
          />,
        );

        leave();
      } catch {
        await showModal(<InvoiceErrorModal />);

        leave();
      }
    },
  });

  return (
    <Page>
      <Header
        backArrowHref={
          <BackButton
            label={t("Vissza")}
            onClick={leave}
          />
        }
      />
      <PageContent>
        <PageTitle>
          <Trans>Számla generálása</Trans>
        </PageTitle>

        <form.AppField
          name="billingInfo.email"
          children={(f) => (
            <f.TextField
              tabIndex={1}
              label={t("E-mail cím * (ahova a számla érkezzen)")}
              type="email"
              placeholder={t("E-mail cím")}
            />
          )}
        />
        <BillingInfoFormFields
          form={form}
          fields={"billingInfo"}
          disabled={false}
          tabIndexStart={2}
        />
        <div className="flex gap-4 mx-auto mt-8 w-fit">
          <Button
            variant="outline"
            onClick={() =>
              then === "start"
                ? leave()
                : (document.location.href = "/bubbles-loves-you-screen")
            }
          >
            <Trans>Számla kihagyása</Trans>
          </Button>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button
                disabled={isSubmitting}
                className="flex gap-4"
                onClick={() => {
                  console.log(form.state.errors);
                  form.handleSubmit();
                }}
              >
                <Trans>Számla generálása</Trans>
              </Button>
            )}
          </form.Subscribe>
        </div>
      </PageContent>
      <Footer />
    </Page>
  );
}
